import React, { useState, useEffect, useCallback } from 'react';
import DocsCreator from './docs-creator';
import Toolbox from '../new-toolbox';
import PagesPanel from '../pages-panel';
import {
  Bold,
  Italic,
  Underline,
  Highlighter,
  FileText,
  Save,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
} from 'lucide-react';
import RichTextToolbar from '../rich-text-toolbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { debounce, update } from 'lodash';
import { useToast } from '../../../Utils/toastContext';

function IconBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`
        group relative flex items-center justify-center
        w-9 h-9 rounded-lg transition-all duration-200
        hover:bg-[#375AE7]/8 hover:scale-[1.04]
        active:scale-95
        ${
          active
            ? 'bg-[#375AE7]/12 text-[#375AE7] shadow-sm ring-1 ring-[#375AE7]/20'
            : 'text-slate-500 hover:text-slate-700'
        }
      `}
    >
      <Icon size={16} strokeWidth={active ? 2.5 : 2} />

      <span
        className="
          absolute -bottom-8 left-1/2 -translate-x-1/2 hidden group-hover:block
          text-[10px] font-medium bg-slate-800 text-white px-2.5 py-1 rounded-md
          whitespace-nowrap shadow-lg z-20
        "
      >
        {label}
      </span>
    </button>
  );
}

function TopBar({ editor, data, oncomment, docName, setDocName }) {
  if (!editor) return null;

  return (
    <div
      className="
        sticky top-0 z-10 shrink-0
        flex flex-wrap items-center justify-between gap-3
        px-4 py-2.5 sm:px-5
        bg-white/80 backdrop-blur-xl
        border-b border-slate-200/80
        shadow-[0_1px_12px_rgba(55,90,231,0.04)]
      "
    >
      {/* LEFT - Document identity */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#375AE7] text-white shadow-md shadow-[#375AE7]/25">
          <FileText className="h-4 w-4" strokeWidth={2.25} />
        </div>

        <div className="leading-tight min-w-0">
          <input
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
            className="
  truncate text-sm font-semibold text-slate-800
  bg-transparent outline-none
  border-b border-transparent
  hover:border-slate-300 focus:border-[#375AE7]
  focus:ring-0 transition-all
  w-full max-w-[260px]
  px-1 py-0.5 rounded
"
            placeholder="Untitled Document"
          />
        </div>
      </div>

      {/* CENTER - Formatting toolbar */}

      <RichTextToolbar
        editor={editor}
        IconBtn={IconBtn}
        oncomment={oncomment}
        icons={{
          Bold,
          Italic,
          Underline,
          Highlighter,
          Heading1,
          Heading2,
          Heading3,
          AlignLeft,
          AlignCenter,
          AlignRight,
          AlignJustify,
          List,
          ListOrdered,
          IndentIncrease,
          IndentDecrease,
        }}
      />

      {/* RIGHT - Save */}
    </div>
  );
}

export default function TemplateCreator() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    pages: [],
  });
  const [editor, setEditor] = useState(null);
  const [activePage, setActivePage] = useState('page-1');
  const location = useLocation();
  const [commentHandler, setCommentHandler] = useState(null);
  const [contractId, setContractId] = useState(null);
  const template =
    location.state && location.state.template ? location.state.template : null;
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const [docName, setDocName] = useState('');
  console.log('Received template:', template);

  useEffect(() => {
    if (template && template.pages && template.pages.length) {
      setData(template);
      setActivePage(template.pages[0].id);
    } else {
      // ✅ Fallback: create default page
      const defaultPage = {
        id: 'page-1',
        html: '',
        content: null,
      };

      setData({ pages: [defaultPage] });
      setActivePage(defaultPage.id);
    }
  }, [template]);

  useEffect(() => {
    if (!data || !data.pages || !data.pages.length) return;
    const exists = data.pages.some((p) => p.id === activePage);
    if (!exists) {
      setActivePage(data.pages[0].id);
    }
  }, [data.pages, activePage]);

  useEffect(() => {
    const handleUpdate = (e) => {
      const pages = e.detail?.pages;
      if (!pages) return;
      setData((prev) => ({ ...prev, pages }));
      if (!pages.some((p) => p.id === activePage) && pages[0])
        setActivePage(pages[0].id);
    };

    window.addEventListener('rp:updatePages', handleUpdate);
    window.addEventListener('rp:addPage', handleUpdate);
    return () => {
      window.removeEventListener('rp:updatePages', handleUpdate);
      window.removeEventListener('rp:addPage', handleUpdate);
    };
  }, [activePage]);
  const saveDocAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/agency/contract-library`;
    const payload = {
      document_content: {
        pages: data.pages,
      },
      title: docName || 'Untitled Document',
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const updateDocAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${contractId}`;
    const payload = {
      document_content: {
        pages: data.pages,
      },
      title: docName || 'Untitled Document',
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const publishDocAPI = async (contractId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${contractId}/publish`;
    const payload = {
      document_content: {
        pages: data.pages,
      },
      title: docName || 'Untitled Document',
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleSaveDocument = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await (contractId ? updateDocAPI() : saveDocAPI());
        if (response.status == 201 || response.status == 200) {
          showToast(response.message, 'success');
          setShowSaveModal(false);
          setContractId(response.data.contract.id);
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [data, docName, contractId],
  );
  const handleSavePublishDocument = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await (contractId ? updateDocAPI() : saveDocAPI());
        console.log('my response is ', response.status);
        if (response.status == 201 || response.status == 200) {
          const responsePublish = await publishDocAPI(
            response.data.contract.id,
          );
          if (responsePublish.status == 200) {
            showToast(response.message, 'success');
            navigate('/document-management');
          }
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [data, docName, contractId],
  );
  return (
    <div className="h-screen rounded-md mr-4 flex bg-gradient-to-br from-slate-100 via-[#F5F7FE]/30 to-slate-100 text-slate-800 overflow-hidden">
      {/* LEFT - Pages */}
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="hidden md:flex md:w-64 shrink-0 flex-col bg-white border-r border-slate-200/80 shadow-sm">
        <PagesPanel
          pages={(data && data.pages) || []}
          activePage={activePage}
          setActivePage={setActivePage}
          isEdit={true}
        />
      </div>

      {/* CENTER - Document stage */}
      <div className="flex-1 relative overflow-hidden flex flex-col min-w-0">
        <TopBar
          editor={editor}
          data={data}
          oncomment={commentHandler}
          docName={docName}
          setDocName={setDocName}
        />
        <div className="flex-1 overflow-auto px-4 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10 flex justify-center">
          <div className="relative w-full max-w-[950px] mx-auto">
            <DocsCreator
              data={data}
              setData={setData}
              activePage={activePage}
              onEditorReady={setEditor}
              setActivePage={setActivePage}
              setCommentHandler={setCommentHandler}
            />
          </div>
        </div>
        <div className="flex justify-end mt-4 px-6">
          <button
            type="button"
            onClick={(e) => {
              if (docName) {
                const payload = {
                  ...data,
                  pages: data.pages,
                  name: docName || 'Untitled Document',
                };

                localStorage.setItem('contract-doc', JSON.stringify(payload));
                setLoading(true);
                handleSaveDocument(e);
              } else {
                setIsDraft(true);
                setShowSaveModal(true);
              }
            }}
            className="
      inline-flex items-center gap-1.5
      px-3.5 py-2 text-xs font-semibold
      bg-[#375AE7] text-white
      rounded-lg shadow-md shadow-[#375AE7]/25
      hover:bg-[#2E4BC4] active:scale-[0.98]
      transition-all mr-4
    "
          >
            <Save className="h-3.5 w-3.5" />
            Save as draft
          </button>
          <button
            type="button"
            onClick={(e) => {
              if (docName) {
                const payload = {
                  ...data,
                  pages: data.pages,
                  name: docName || 'Untitled Document',
                };

                localStorage.setItem('contract-doc', JSON.stringify(payload));
                setLoading(true);
                handleSavePublishDocument(e);
              } else {
                setShowSaveModal(true);
              }
            }}
            className="
      inline-flex items-center gap-1.5
      px-3.5 py-2 text-xs font-semibold
      bg-[#375AE7] text-white
      rounded-lg shadow-md shadow-[#375AE7]/25
      hover:bg-[#2E4BC4] active:scale-[0.98]
      transition-all
    "
          >
            <Save className="h-3.5 w-3.5" />
            Save & Publish
          </button>
        </div>
      </div>

      {/* RIGHT - Toolbox */}
      <div className="hidden md:flex md:w-72 shrink-0 flex-col overflow-hidden border-l border-slate-200/80 shadow-sm">
        <Toolbox editor={editor} />
      </div>
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSaveModal(false)}
          />

          {/* modal */}
          <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-lg font-semibold text-slate-800">
              Save Document
            </h2>

            <p className="text-xs text-slate-500 mt-1">
              Enter a name for your document before saving.
            </p>

            <input
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              placeholder="e.g. Contract Draft v1"
              className="
          mt-4 w-full px-4 py-2.5 text-sm
          border border-slate-200 rounded-xl
          outline-none focus:ring-2 focus:ring-[#375AE7]/30
          focus:border-[#375AE7]
        "
            />

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>

              <button
                onClick={(e) => {
                  if (!docName) {
                    showToast('Plaese enter the document name', 'error');
                    return;
                  }
                  const payload = {
                    ...data,
                    pages: data.pages,
                    name: docName || 'Untitled Document',
                  };

                  localStorage.setItem('contract-doc', JSON.stringify(payload));
                  setLoading(true);
                  console.log('before save', contractId);
                  if (isDraft) {
                    handleSaveDocument(e);
                  } else {
                    handleSavePublishDocument(e);
                  }
                }}
                className="
            px-4 py-2 text-xs font-semibold
            bg-[#375AE7] text-white rounded-xl
            hover:bg-[#2E4BC4]
            transition-all cursor-pointer
          "
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
